/**
 * `<EmptyWorkspace/>` — the fresh-Tasks-team empty state (B1.3).
 *
 * Issue #759 (Phase 2). When a team has zero nodes, the whole product
 * starts from one prompt: name the outcome. Focal element = the single
 * input (no grid of empty widgets — §A1 / `feedback_ui_section_focal_
 * component.md`). Submitting creates the first root node (POST /nodes, no
 * parent). This is the screen a freshly-created Map team lands on, so the
 * copy + flow are real, not placeholder.
 *
 * Copy is the gallery's, verbatim: "Start with the outcome." etc.
 *
 * Editorial revamp (#1044, PR-0 proof view): this is the first view migrated
 * off the static `PALETTE` onto `useMapTheme()`. With the per-team
 * `task_editorial_ui` flag OFF (every team, today) it renders byte-identical
 * to the shipped soft-mint look; with the flag ON it renders the warm
 * editorial wireframe (serif-italic hero, ink "Add" button, cream paper).
 * Same JSX, same data-testids, same copy — only the look forks on theme.
 */

import { type FormEvent, useState } from "react";

import { useMapTheme } from "../theme/MapThemeContext";

export function EmptyWorkspace({
  onCreateRoot,
  pending,
  onStartFromExample,
}: {
  onCreateRoot: (title: string) => void;
  pending: boolean;
  /**
   * Open the worked-example picker (#985). The blank start stays the PRIMARY
   * path; this is the alternative for a lead who'd rather learn decomposition
   * from an example. Adopting is lead-only (server-enforced); the entry is
   * lead-gated by its presence — `MapEditor` passes this only from the lead
   * editor, so a non-lead never sees the affordance. Omitted ⇒ no example link.
   */
  onStartFromExample?: () => void;
}) {
  const [value, setValue] = useState("");
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const title = value.trim();
    if (title) onCreateRoot(title);
  };

  return (
    <div
      data-testid="empty-workspace"
      className="flex h-full w-full flex-col items-center justify-center px-6 text-center"
      style={{ backgroundColor: c.canvas }}
    >
      <div className="w-full max-w-2xl">
        {/* A short, calm "what Map is for" line (#980) — a first-timer lands here
            with no idea what the surface does. One factual sentence frames it
            before the focal prompt; it never competes with the input below. */}
        <p
          data-testid="empty-workspace-explainer"
          className="mb-2 text-[11px] font-semibold uppercase tracking-wide"
          style={{
            color: editorial ? c.muted : c.faint,
            fontFamily: editorial ? theme.fonts.mono : undefined,
          }}
        >
          Map turns one outcome into a shared tree of work everyone can see.
        </p>
        <h1
          className={editorial ? "text-4xl font-normal" : "text-2xl font-bold"}
          style={{
            color: c.ink,
            fontFamily: editorial ? theme.fonts.serif : undefined,
            fontStyle: editorial ? "italic" : undefined,
            letterSpacing: editorial ? "-0.015em" : undefined,
          }}
        >
          Start with the outcome.
        </h1>
        <p className="mt-1 text-sm" style={{ color: c.muted }}>
          What is this team trying to deliver?
        </p>

        <form onSubmit={submit} className="mt-6 flex items-center justify-center gap-3">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={'e.g. "Publish the cardio-imaging study"'}
            data-testid="empty-workspace-input"
            aria-label="Name the outcome"
            className={`h-12 w-full max-w-md bg-white px-4 text-sm outline-none ${
              editorial ? "rounded-md border" : "rounded-lg border-2"
            }`}
            style={{
              borderColor: editorial ? c.rule2 : c.teal,
              color: c.ink,
              fontFamily: editorial ? theme.fonts.sans : undefined,
            }}
          />
          <button
            type="submit"
            data-testid="empty-workspace-add"
            disabled={pending || value.trim().length === 0}
            className={`h-12 shrink-0 px-6 text-sm font-semibold transition-opacity disabled:opacity-50 ${
              editorial ? "rounded-md" : "rounded-lg"
            }`}
            style={{
              backgroundColor: editorial ? c.ink : c.teal,
              color: editorial ? c.canvas : "#FFFFFF",
            }}
          >
            Add
          </button>
        </form>

        <p
          className="mt-4 text-xs"
          style={{
            color: editorial ? c.muted : c.faint,
            fontStyle: editorial ? "italic" : undefined,
          }}
        >
          Then break it down — type sub-tasks under it, or switch to the tree to draw it out.
        </p>

        {/* "Start from an example →" (#985) — the SECONDARY path, alongside (never
            replacing) the focal blank-start above. A lead who'd rather learn
            decomposition by example opens the worked-example picker. Lead-gated:
            it renders only when the lead editor passes `onStartFromExample`, so
            a non-lead never sees it (adopting is lead-only, server-enforced). */}
        {onStartFromExample && (
          <div className="mt-3 flex items-center justify-center gap-2 text-xs">
            <span style={{ color: c.faint }}>Not sure where to start?</span>
            <button
              type="button"
              data-testid="empty-workspace-from-example"
              onClick={onStartFromExample}
              className="font-semibold underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
              style={{ color: c.teal }}
            >
              Start from an example →
            </button>
          </div>
        )}

        {/* One-time coach-mark: the four-step loop, calm + brief (B1.3 #932). */}
        <div
          data-testid="empty-workspace-coachmark"
          className="mt-8 w-full rounded-xl border px-6 py-4 text-left"
          style={{
            borderColor: editorial ? c.rule : c.softBorder,
            backgroundColor: editorial ? c.cardBg : "#F7FFF9",
          }}
        >
          <p
            className="mb-3 text-[11px] font-semibold uppercase tracking-wide"
            style={{
              color: editorial ? c.muted : c.teal,
              fontFamily: editorial ? theme.fonts.mono : undefined,
            }}
          >
            How it works
          </p>
          <ol className="flex flex-col gap-2">
            {COACH_STEPS.map((step, i) => (
              <li key={step.label} className="flex items-start gap-3">
                <span
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                  style={{
                    backgroundColor: editorial ? c.ink : c.teal,
                    color: editorial ? c.canvas : "#FFFFFF",
                  }}
                >
                  {i + 1}
                </span>
                <div>
                  <span className="text-[12px] font-semibold" style={{ color: c.ink }}>
                    {step.label}
                  </span>
                  <span className="ml-1.5 text-[12px]" style={{ color: c.muted }}>
                    {step.detail}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

const COACH_STEPS = [
  { label: "Name the outcome.", detail: "Type the goal this team is working toward." },
  { label: "Break it down.", detail: "Add sub-tasks under it — as many layers deep as you need." },
  {
    label: "Assign people.",
    detail: "Put each piece on the right person so everyone knows their part.",
  },
  {
    label: "Move state as work moves.",
    detail: "Mark tasks in progress or done; the map stays current.",
  },
] as const;
